import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";
import { db, doc, updateDoc, storage } from "../../../../firebaseConfig";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import convertHeicToJpg from "../../../../Utils/convertHeicToJpg";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import DownloadIcon from "@mui/icons-material/Download";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import debounce from "lodash.debounce";
import SetupImageLightbox from "./SetupImageLightbox";



const { DateTime } = require("luxon");

const SetupAccordionV2 = ({
  setup,
  defaultExpanded,
  setups,
  setSetups,
  clientData,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState(setup.name || "");
  const [contextMenu, setContextMenu] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isEipUploading, setIsEipUploading] = useState(false);
  const [selectedEip, setSelectedEip] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [confirmItemDeleteOpen, setConfirmItemDeleteOpen] = useState(false);
  const [localNotes, setLocalNotes] = useState(setup.notes || "");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [downloadingAll, setDownloadingAll] = useState(false);
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const debouncedUpdateNotes = useMemo(
    () =>
      debounce((notes) => {
        const newSetups = JSON.parse(JSON.stringify(setups));
        const studioKey = Object.keys(newSetups).find((key) =>
          newSetups[key].some((s) => s.id === setup.id)
        );
        if (studioKey) {
          const setupIndex = newSetups[studioKey].findIndex(
            (s) => s.id === setup.id
          );
          if (setupIndex !== -1) {
            newSetups[studioKey][setupIndex].notes = notes;
            setSetups(newSetups);
            updateDoc(doc(db, "ClientDatabase", clientData.id), { setups: newSetups });
          }
        }
      }, 2000),
    [setups, setup.id, clientData.id, setSetups]
  );

  useEffect(() => {
    setLocalNotes(setup.notes || "");
  }, [setup.notes]);

  const normalizedImages = useMemo(() => {
    return (setup.images || [])
      .map((image, index) => {
        const url =
          typeof image === "string"
            ? image
            : image?.url || image?.src || "";
        if (!url) return null;
        const title =
          typeof image === "string"
            ? `Image ${index + 1}`
            : image?.name || image?.title || `Image ${index + 1}`;
        return {
          url,
          title,
          raw: image,
        };
      })
      .filter(Boolean);
  }, [setup.images]);

  useEffect(() => {
    if (lightboxIndex >= normalizedImages.length && normalizedImages.length) {
      setLightboxIndex(normalizedImages.length - 1);
    }
  }, [lightboxIndex, normalizedImages.length]);

  const handleOpenLightbox = useCallback(
    (index) => {
      if (!normalizedImages.length) return;
      const safeIndex = Math.min(Math.max(index, 0), normalizedImages.length - 1);
      setLightboxIndex(safeIndex);
      setLightboxOpen(true);
    },
    [normalizedImages.length]
  );

  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handleNotesChange = (e) => {
    const newValue = e.target.value;
    setLocalNotes(newValue);
    debouncedUpdateNotes(newValue);
  };

  const updateSetups = (updateFn) => {
    const newSetups = JSON.parse(JSON.stringify(setups));
    const studioKey = Object.keys(newSetups).find((key) =>
      newSetups[key].some((s) => s.id === setup.id)
    );
    if (studioKey) {
      const setupIndex = newSetups[studioKey].findIndex(
        (s) => s.id === setup.id
      );
      if (setupIndex !== -1) {
        updateFn(newSetups[studioKey][setupIndex]);
        setSetups(newSetups);
        updateDoc(doc(db, "ClientDatabase", clientData.id), { setups: newSetups });
      }
    }
  };

  const handleRename = () => {
    if (!newName.trim()) return;
    
    updateSetups((setupToUpdate) => {
      setupToUpdate.name = newName.trim();
    });
    
    setRenameDialogOpen(false);
  };

  const handleDelete = () => {
    const newSetups = JSON.parse(JSON.stringify(setups));
    const studioKey = Object.keys(newSetups).find((key) =>
      newSetups[key].some((s) => s.id === setup.id)
    );
    if (studioKey) {
      newSetups[studioKey] = newSetups[studioKey].filter(
        (s) => s.id !== setup.id
      );
      setSetups(newSetups);
      updateDoc(doc(db, "ClientDatabase", clientData.id), { setups: newSetups });
    }
    setConfirmDeleteOpen(false);
  };

  const handleContextMenu = (event, item, type) => {
    event.preventDefault();
    if (type === "eip") {
      setSelectedEip(item);
    } else {
      setSelectedImage(item);
    }
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedEip(null);
    setSelectedImage(null);
  };

  const handleConfirmItemDelete = () => {
    const field = selectedEip ? "eips" : "images";
    const item = selectedEip || selectedImage;
    // setups are deep-cloned, so match by url (legacy entries are plain strings)
    const key = (entry) => entry?.url ?? entry?.src ?? entry;
    if (item) {
      updateSetups((setupToUpdate) => {
        setupToUpdate[field] = (setupToUpdate[field] || []).filter(
          (entry) => key(entry) !== key(item)
        );
      });
      // Only files we uploaded live in Firebase Storage; legacy entries may be Dropbox links or text
      if (item.url?.includes("firebasestorage.googleapis.com")) {
        deleteObject(storageRef(storage, item.url)).catch((error) =>
          console.error("Error deleting file from storage:", error)
        );
      }
    }
    setConfirmItemDeleteOpen(false);
    handleCloseContextMenu();
  };

  const triggerFileDownload = useCallback((href, fileName, revokeAfter = false) => {
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", fileName);
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (revokeAfter) {
      setTimeout(() => URL.revokeObjectURL(href), 0);
    }
  }, []);

  const handleDownloadImage = useCallback(
    async (imageMeta) => {
      if (!imageMeta) return;
      const resolveUrl = () => {
        if (typeof imageMeta === "string") return imageMeta;
        if (imageMeta.url) return imageMeta.url;
        if (imageMeta.src) return imageMeta.src;
        if (typeof imageMeta.raw === "string") return imageMeta.raw;
        return imageMeta.raw?.url || imageMeta.raw?.src || null;
      };
      const sourceUrl = resolveUrl();
      if (!sourceUrl) return;

      const fileName =
        imageMeta.title ||
        imageMeta.name ||
        imageMeta?.raw?.name ||
        imageMeta?.raw?.title ||
        `setup-image-${Date.now()}.jpg`;

      const downloadDirect = (href) => triggerFileDownload(href, fileName, false);

      if (sourceUrl.startsWith("data:") || sourceUrl.startsWith("blob:")) {
        downloadDirect(sourceUrl);
        return;
      }

      try {
        const response = await fetch(sourceUrl, { cache: "no-cache" });
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        triggerFileDownload(blobUrl, fileName, true);
      } catch (error) {
        console.error("Failed to download image, opening in new tab", error);
        window.open(sourceUrl, "_blank", "noopener,noreferrer");
      }
    },
    [triggerFileDownload]
  );

  const handleDownloadAllImages = useCallback(async () => {
    if (!normalizedImages.length || downloadingAll) return;
    setDownloadingAll(true);
    try {
      for (const imageMeta of normalizedImages) {
        // eslint-disable-next-line no-await-in-loop
        await handleDownloadImage(imageMeta);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    } finally {
      setDownloadingAll(false);
    }
  }, [downloadingAll, handleDownloadImage, normalizedImages]);

  const handleDownloadEip = useCallback(
    (eip, index) => {
      if (!eip) return;

      const fallbackBase = (setup.name || "setup").replace(/\s+/g, "_");
      const fallbackName = `${fallbackBase}_EIP_${index + 1}.eip`;
      const fileName =
        typeof eip === "string"
          ? fallbackName
          : eip?.name || eip?.fileName || fallbackName;

      const triggerDownload = (url, revokeAfter = false, openInNewTab = false) => {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        if (openInNewTab) {
          link.rel = "noopener noreferrer";
          link.target = "_blank";
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        if (revokeAfter) {
          setTimeout(() => URL.revokeObjectURL(url), 0);
        }
      };

      const remoteUrl =
        typeof eip === "string"
          ? null
          : eip?.downloadUrl || eip?.url || eip?.href || null;

      if (remoteUrl) {
        triggerDownload(remoteUrl, false, true);
        return;
      }

      const textContent =
        typeof eip === "string" ? eip : eip?.content || eip?.text || "";
      if (textContent) {
        const blob = new Blob([textContent], { type: "text/plain" });
        const blobUrl = URL.createObjectURL(blob);
        triggerDownload(blobUrl, true, false);
      }
    },
    [setup.name]
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  const accordionHeaderDate = setup.date
    ? DateTime.fromISO(setup.date).setZone("Australia/Sydney").toFormat("dd/MM/yyyy")
    : "No date";

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Accordion
        defaultExpanded={defaultExpanded}
        sx={{
          background:
            theme === "dark"
              ? "rgba(255, 255, 255, 0.03)"
              : "rgba(30, 30, 30, 0.03)",
          mb: 1,
          borderRadius: "8px !important",
          overflow: "hidden",
          boxShadow:
            theme === "dark"
              ? "0 2px 8px rgba(0,0,0,0.3)"
              : "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {setup.name || "Untitled Setup"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {accordionHeaderDate}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setNewName(setup.name || "");
                  setRenameDialogOpen(true);
                }}
                sx={{
                  color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              {isAdmin && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteOpen(true);
                }}
                disabled
                sx={{
                  color:
                    theme === "dark"
                      ? "rgba(255,100,100,0.3)"
                      : "rgba(200,50,50,0.3)",
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              )}
            </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 2 }}>
          <motion.div variants={itemVariants}>
            {/* EIPs Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                EIPs
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {setup.eips?.map((eip, index) => (
                  <Tooltip key={index} title="Download EIP">
                    <Box
                      component="button"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDownloadEip(eip, index);
                      }}
                      onContextMenu={(e) => handleContextMenu(e, eip, "eip")}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(100, 150, 255, 0.18)"
                            : "rgba(100, 150, 255, 0.08)",
                        border: `1px solid ${
                          theme === "dark"
                            ? "rgba(100, 150, 255, 0.3)"
                            : "rgba(100, 150, 255, 0.3)"
                        }`,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: theme === "dark" ? "#fff" : "#000",
                        minWidth: 120,
                        textAlign: "left",
                        transition: "border-color 0.2s ease, background 0.2s ease",
                        "&:hover": {
                          borderColor:
                            theme === "dark"
                              ? "rgba(120, 170, 255, 0.6)"
                              : "rgba(80, 120, 200, 0.7)",
                        },
                        "&:focus-visible": {
                          outline: "2px solid rgba(100,150,255,0.5)",
                          outlineOffset: "2px",
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          flex: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {typeof eip === "string"
                          ? eip
                          : eip?.name || eip?.fileName || "EIP"}
                      </Typography>
                      <DownloadIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
              <Button
                  size="small"
                  startIcon={isEipUploading ? <CircularProgress size={16} /> : <AddIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  variant="outlined"
                  disabled={isEipUploading}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                  }}
                >
                  {isEipUploading ? "Uploading..." : "Add EIP"}
                </Button>
            </Box>

            {/* Images Section */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Images
                </Typography>
                {normalizedImages.length > 0 && (
                  <Tooltip title="Download all images">
                    <Button
                      size="small"
                      startIcon={
                        downloadingAll ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <CloudDownloadIcon />
                        )
                      }
                      disabled={downloadingAll}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDownloadAllImages();
                      }}
                      sx={{ textTransform: "none", borderRadius: "8px" }}
                    >
                      {downloadingAll ? "Downloading..." : "Download All"}
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                {normalizedImages.map((imageMeta, index) => {
                  const { url, title, raw } = imageMeta;
                  return (
                    <Box
                      key={index}
                      onContextMenu={(e) => handleContextMenu(e, raw, "image")}
                      onClick={() => handleOpenLightbox(index)}
                      sx={{
                        width: 110,
                        height: 110,
                        borderRadius: "8px",
                        overflow: "hidden",
                        cursor: "pointer",
                        border: `2px solid ${
                          theme === "dark"
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.1)"
                        }`,
                        position: "relative",
                        "&:hover": {
                          border: `2px solid ${
                            theme === "dark"
                              ? "rgba(100,150,255,0.5)"
                              : "rgba(100,150,255,0.7)"
                          }`,
                        },
                      }}
                    >
                      <img
                        src={url}
                        alt={title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                        void handleDownloadImage(imageMeta);
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          width: 28,
                          height: 28,
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.75)",
                          },
                        }}
                      >
                        <DownloadIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
              <Button
                  size="small"
                  startIcon={isImageUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                  onClick={() => imageInputRef.current?.click()}
                  variant="outlined"
                  disabled={isImageUploading}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                  }}
                >
                  {isImageUploading ? "Uploading..." : "Add Images"}
                </Button>
            </Box>

            {/* Notes Section */}
            <Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={localNotes}
                onChange={handleNotesChange}
                variant="filled"
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiFilledInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(45,45,45,0.7)"
                        : "rgba(250,250,250,0.9)",
                    "&:hover": {
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(55,55,55,0.8)"
                          : "rgba(255,255,255,1)",
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(60,60,60,0.9)"
                          : "rgba(255,255,255,1)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0 0 2px rgba(100,100,255,0.5)"
                          : "0 0 0 2px rgba(100,100,255,0.3)",
                    },
                  },
                }}
              />
            </Box>
          </motion.div>
        </AccordionDetails>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".txt,.eip"
          multiple
          onChange={async (e) => {
            const files = Array.from(e.target.files);
            if (files.length === 0) return;

            // EIPs are 15-30MB zips, so they go to Storage, not the Firestore doc
            setIsEipUploading(true);
            try {
              const uploaded = [];
              for (const file of files) {
                const path = `SetupEIPs/${clientData.id}/${setup.id}/${uuidv4().slice(0, 5)}_${file.name}`;
                const fileRef = storageRef(storage, path);
                await uploadBytes(fileRef, file);
                uploaded.push({ url: await getDownloadURL(fileRef), name: file.name });
              }
              updateSetups((setupToUpdate) => {
                setupToUpdate.eips = [...(setupToUpdate.eips || []), ...uploaded];
              });
            } catch (error) {
              console.error("Error uploading EIPs:", error);
              alert("Error uploading EIPs. Please try again.");
            } finally {
              setIsEipUploading(false);
              e.target.value = null;
            }
          }}
        />

        <input
          type="file"
          ref={imageInputRef}
          style={{ display: "none" }}
          accept="image/*,.heic,.heif"
          multiple
          onChange={async (e) => {
            const files = Array.from(e.target.files);
            if (files.length === 0) return;

            setIsImageUploading(true);
            try {
              const uploaded = [];
              for (const original of files) {
                const file = await convertHeicToJpg(original);
                const path = `SetupImages/${clientData.id}/${setup.id}/${uuidv4().slice(0, 5)}_${file.name}`;
                const fileRef = storageRef(storage, path);
                await uploadBytes(fileRef, file);
                uploaded.push({ url: await getDownloadURL(fileRef), name: file.name });
              }
              updateSetups((setupToUpdate) => {
                setupToUpdate.images = [...(setupToUpdate.images || []), ...uploaded];
              });
            } catch (error) {
              console.error("Error uploading images:", error);
              alert("Error uploading images. Please try again.");
            } finally {
              setIsImageUploading(false);
              e.target.value = null;
            }
          }}
        />
      </Accordion>

      {/* Context Menu */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            // Close the menu but keep the selection for the confirm dialog
            setContextMenu(null);
            setConfirmItemDeleteOpen(true);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete {selectedEip ? "EIP" : "Image"}
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Setup</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Setup Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            variant="filled"
            InputProps={{ disableUnderline: true }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRename} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Setup</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this setup? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* EIP / Image Delete Confirmation Dialog */}
      <Dialog open={confirmItemDeleteOpen} onClose={() => { setConfirmItemDeleteOpen(false); handleCloseContextMenu(); }}>
        <DialogTitle>Delete {selectedEip ? "EIP" : "Image"}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>
              {selectedEip
                ? selectedEip?.name || "this EIP"
                : selectedImage?.name || selectedImage?.title || "this image"}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmItemDeleteOpen(false); handleCloseContextMenu(); }}>Cancel</Button>
          <Button onClick={handleConfirmItemDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <SetupImageLightbox
        open={lightboxOpen}
        startIndex={lightboxIndex}
        images={normalizedImages}
        onClose={handleCloseLightbox}
      />
    </motion.div>
  );
};

export default SetupAccordionV2;
